import React, { useState } from 'react';
import { Reveal } from './ScrollReveal';
import { FiCheck, FiArrowRight, FiLoader } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

export default function CTA() {
  const [status, setStatus] = useState('idle'); // 'idle' | 'submitting' | 'submitted' | 'error'

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('submitting');
    
    const form = e.target;
    const formData = new FormData(form);
    
    try {
      const response = await fetch("https://formspree.io/f/mojpnjyn", {
        method: "POST",
        body: formData,
        headers: {
          'Accept': 'application/json'
        }
      });
      
      if (response.ok) {
        setStatus('submitted');
        form.reset();
      } else {
        setStatus('error');
        setTimeout(() => setStatus('idle'), 3000);
      }
    } catch (error) {
      setStatus('error');
      setTimeout(() => setStatus('idle'), 3000);
    }
  };

  return (
    <section id="kontakt" className="bg-brand-surface border-t border-brand-border relative z-10">
      <div className="max-w-[1100px] mx-auto px-8 py-28">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-20 items-start">
          <div>
            <Reveal>
              <div className="text-[0.65rem] font-medium tracking-[0.18em] uppercase text-brand-gold mb-4">Jetzt anfragen</div>
              <h2 className="font-serif text-[clamp(2.2rem,4vw,3.8rem)] font-light text-brand-white leading-[1.1] tracking-[-0.02em] mb-4">
                30 Minuten.<br/><em className="not-italic italic text-brand-gold">Kein Pitch.</em><br/>Nur Klarheit.
              </h2>
              <p className="text-[0.9rem] text-brand-muted leading-[1.8] mb-8">
                Ein ehrliches Gespräch darüber, wo Sie heute stehen — und in welchen der drei Kreise Sie passen. Wenn es nicht passt, sage ich es direkt.
              </p>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="flex flex-col gap-2.5 mb-10">
                {[
                  "Kostenlos & unverbindlich",
                  "Keine Verkaufspräsentation — eine Prüfung auf Passung",
                  "Ehrliches Feedback — auch wenn es kein Ja gibt",
                  "Antwort persönlich, innerhalb von 24 Stunden",
                  "Aktuell in allen Kreisen noch Plätze frei — Warteliste möglich"
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3 text-[0.85rem] text-brand-muted">
                    <span className="text-brand-gold text-[0.8rem] mt-[1px] shrink-0 font-sans">✓</span>
                    {item}
                  </div>
                ))}
              </div>
            </Reveal>

            <Reveal delay={0.2}>
              <div className="pt-6 border-t border-brand-border">
                <div className="text-[0.72rem] text-brand-muted mb-3">Noch nicht bereit für ein Gespräch?</div>
                <a href="#struktur" className="group inline-flex items-center gap-2 text-[0.82rem] text-brand-gold border border-brand-gold3 px-5 py-2.5 rounded-[2px] transition-colors hover:bg-brand-gold/10">
                  Die Struktur nochmal ansehen
                  <SafeIcon icon={FiArrowRight} className="transition-transform group-hover:translate-x-1" />
                </a>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.3}>
            <div className="bg-brand-card border border-brand-border2 p-10">
              <div className="font-serif text-[1.5rem] font-normal text-brand-white mb-2">Erstgespräch anfragen</div>
              <div className="text-[0.8rem] text-brand-muted mb-8 leading-[1.6]">Ich melde mich persönlich innerhalb von 24 Stunden. Kein automatisiertes Follow-up.</div>
              
              <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                {/* Honeypot für Spam-Schutz */}
                <input type="text" name="_gotcha" style={{ display: 'none' }} />
                <input type="hidden" name="_subject" value="Neue Anfrage: Executive Sparring - Harald Suekar" />

                <div className="flex flex-col gap-1.5">
                  <label className="text-[0.7rem] font-medium tracking-[0.1em] uppercase text-brand-muted2">Name</label>
                  <input 
                    required 
                    name="name"
                    type="text" 
                    placeholder="Ihr vollständiger Name" 
                    className="w-full bg-brand-dark border border-brand-border2 text-brand-text font-sans text-[0.9rem] font-light py-3 px-4 outline-none transition-colors focus:border-brand-gold3 placeholder:text-brand-muted2" 
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-[0.7rem] font-medium tracking-[0.1em] uppercase text-brand-muted2">Unternehmen & Rolle</label>
                  <input 
                    required 
                    name="company_role"
                    type="text" 
                    placeholder="z.B. GF bei Muster GmbH" 
                    className="w-full bg-brand-dark border border-brand-border2 text-brand-text font-sans text-[0.9rem] font-light py-3 px-4 outline-none transition-colors focus:border-brand-gold3 placeholder:text-brand-muted2" 
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-[0.7rem] font-medium tracking-[0.1em] uppercase text-brand-muted2">E-Mail</label>
                  <input 
                    required 
                    name="email"
                    type="email" 
                    placeholder="ihre@email.com" 
                    className="w-full bg-brand-dark border border-brand-border2 text-brand-text font-sans text-[0.9rem] font-light py-3 px-4 outline-none transition-colors focus:border-brand-gold3 placeholder:text-brand-muted2" 
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-[0.7rem] font-medium tracking-[0.1em] uppercase text-brand-muted2">In welchem Kreis sehen Sie sich?</label>
                  <select 
                    required 
                    name="target_circle"
                    className="w-full bg-brand-dark border border-brand-border2 text-brand-text font-sans text-[0.9rem] font-light py-3 px-4 outline-none transition-colors focus:border-brand-gold3 cursor-pointer appearance-none"
                  >
                    <option value="" disabled selected>Bitte wählen...</option>
                    <option value="GF & C-Level">GF & C-Level</option>
                    <option value="Gründer & Startup">Gründer & Startup</option>
                    <option value="Solopreneur & Selbstständig">Solopreneur & Selbstständig</option>
                    <option value="Ich bin nicht sicher">Ich bin nicht sicher</option>
                  </select>
                </div>

                <div className="flex flex-col gap-1.5 mb-2">
                  <label className="text-[0.7rem] font-medium tracking-[0.1em] uppercase text-brand-muted2">Was bringt Sie hierher?</label>
                  <textarea 
                    required 
                    name="message"
                    rows="3" 
                    placeholder="Kurze Beschreibung Ihrer aktuellen Situation..." 
                    className="w-full bg-brand-dark border border-brand-border2 text-brand-text font-sans text-[0.9rem] font-light py-3 px-4 outline-none transition-colors focus:border-brand-gold3 placeholder:text-brand-muted2 resize-y"
                  ></textarea>
                </div>

                <button 
                  type="submit" 
                  disabled={status === 'submitting' || status === 'submitted'} 
                  className={`w-full font-sans text-[0.75rem] font-medium tracking-[0.12em] uppercase py-3.5 px-4 transition-all mt-2 flex items-center justify-center gap-2 ${
                    status === 'submitted' 
                      ? 'bg-[#5cb88a] text-brand-black cursor-default' 
                      : status === 'error'
                        ? 'bg-red-500 text-white'
                        : 'bg-brand-gold text-brand-black hover:bg-brand-gold2 cursor-pointer shadow-lg active:translate-y-0.5'
                  }`}
                >
                  {status === 'submitting' && <SafeIcon icon={FiLoader} className="animate-spin" />}
                  {status === 'submitted' && 'Anfrage gesendet ✓'}
                  {status === 'error' && 'Fehler beim Senden'}
                  {status === 'idle' && 'Gespräch anfragen — kostenlos & unverbindlich →'}
                </button>

                <div className="text-[0.7rem] text-brand-muted2 mt-1 leading-[1.5] text-center">
                  30 Minuten · kostenlos · kein Pitch · Antwort in 24h
                </div>
              </form>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}